<template>
  <div class="testAccount">
    <el-row :gutter="20">
      <el-col
        :span="4"
        class="stepTitle"
      >
        <div>
          <span class="label">
            {{ $t('testAccount.title') }}
          </span>
          <el-tooltip
            effect="dark"
            popper-class="urlTip"
            :content="testAccountTip"
          >
            <i class="el-icon-question mr "></i>
          </el-tooltip>
        </div>
      </el-col>
      <el-col
        :span="17"
        :offset="2"
      >
        <div class="content">
          <div class="lineHeight-40">{{ $t('testAccount.instruction') }}</div>
          <div class="accountList">
            <el-row
              class="accountItem"
              :gutter="20"
              v-for="(account, index) in accountList"
              :key="account.id"
            >
              <el-col :span="1.5">
                <span class="accountIndex">{{ index > 9 ? `${index + 1}` : `0${index + 1}` }}</span>
              </el-col>
              <el-col
                :span="1.5"
                class="lineHeight-40"
              >{{ $t('testAccount.role') }}</el-col>
              <el-col :span="7">
                <el-select
                  style="display: block;"
                  :ref="`roleSelect-${index}`"
                  v-model="account.role"
                  filterable
                  allow-create
                  default-first-option
                  :disabled="!!account.sessionInfo"
                  :placeholder="$t('testAccount.selectRole')"
                  automatic-dropdown
                >
                  <el-option
                    v-for="item in options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  >
                  </el-option>
                </el-select>
              </el-col>
              <el-col
                style="display: flex;"
                :span="6"
              >
                <el-button
                  :disabled="verifyDisabled"
                  type="success"
                  @click="handleVerify(account, index)"
                >
                  {{ $t('testAccount.verifyLogin') }}
                </el-button>
                <i
                  style="font-size: 20px; line-height: 40px; margin-left: 20px; margin-right: 8px;"
                  :class="['el-icon-success', 'accountIcon', {active: account.sessionInfo}]"
                ></i>
                <el-button
                  @click="handleDeleteAccount(index, account.id)"
                  style="height: 40px;position:relative;bottom: 4px;"
                  type="text"
                >
                  <i class="el-icon-delete-solid delIcon"></i>
                </el-button>
              </el-col>
            </el-row>
            <div>
              <el-tooltip
                :disabled="!addDisabled"
                placement="bottom"
                popper-class="verifyTip"
                :content="$t('testAccount.verifyTip')"
              >
                <el-button
                  type="text"
                  class="addAccount"
                  :disabled="addDisabled"
                  @click="handleAddAccount"
                >
                  <i class="el-icon-circle-plus"></i>
                  {{ $t('testAccount.addAccount') }}
                </el-button>
              </el-tooltip>
            </div>
          </div>
          <div v-if="extensionInstalled && !needToUpgrade && !isAllowedIncognitoAccess">
            <div class="incognito">
              <el-tooltip
                placement="top"
                popper-class="incognitoTip"
              >
                <div slot="content">
                  <div class="incognitoTipText">
                    <span>{{ $t('testAccount.incognitoInstructions.step1') }}</span>
                  </div>
                  <div class="incognitoTipText">
                    <span>{{ $t('testAccount.incognitoInstructions.step2') }}</span>
                  </div>
                  <div class="incognitoTipText">
                    <span>{{ $t('testAccount.incognitoInstructions.step3', { EXTENSION_NAME: EXTENSION_NAME }) }}</span>
                  </div>
                  <div class="incognitoTipText">
                    <span>{{ $t('testAccount.incognitoInstructions.step4') }}</span>
                  </div>
                </div>
                <i class="el-icon-question warningIcon"></i>
              </el-tooltip>
              <span class="uninstallText">{{ $t('testAccount.incognitoWarning', { EXTENSION_NAME: EXTENSION_NAME }) }}</span>
              <el-button
                size="mini"
                type="warning"
                plain
                @click="handleCheckAgain"
              >
                {{ $t('testAccount.checkAgain') }}({{count}}s)
              </el-button>
            </div>
          </div>
          <div
            class="uninstall"
            v-if="!extensionInstalled || needToUpgrade"
          >
            <div>
              <el-tooltip
                placement="top"
                popper-class="urlTip"
                :content="$t('testAccount.tip', { EXTENSION_NAME: EXTENSION_NAME })"
              >
                <i class="el-icon-question warningIcon"></i>
              </el-tooltip>
              <span class="uninstallText">
                {{ extensionInstalled 
                  ? $t('testAccount.extensionNeedUpgrade', { version, nextVersion }) 
                  : $t('testAccount.extensionNotInstalled') }}
              </span>
            </div>
            <el-tag
              color="#E1E8ED"
              class="uninstallTag"
            >
              <i18n path="testAccount.installExtension">
                <a
                  class="link"
                  target="_blank"
                  :href="extensionDownloadUrl"
                >
                  {{ $t('testAccount.installExtensionLink') }}
                </a>
              </i18n>
            </el-tag>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import eventBus, { EventType } from '@/lib/events';
import { RoleOptions } from '@/enum';

export default {
  name: 'TestAccount',
  inject: ['taskData'],
  props: {
    payload: Object,
  },
  data() {
    const { accountList = [], loginUrl = '' } = this.payload || {};

    return {
      homePageUrl: loginUrl,
      nextVersion: process.env.VUE_APP_EXTENSION_VERSION,
      showSkipAccount: true,
      skipAccount: false,
      extensionDownloadUrl: process.env.VUE_APP_EXTENSION_DOWNLOAD_URL,
      testAccountTip: this.$t('testAccount.tip', { EXTENSION_NAME: process.env.VUE_APP_EXTENSION_NAME }),
      accountList: accountList.length
        ? accountList
        : [
            {
              role: '',
              id: crypto.randomUUID(),
              sessionInfo: null,
            },
          ],
      options: RoleOptions,
      skip: false,
      count: 10,
      loginUrl,
    };
  },
  computed: {
    ...mapState('common', ['extensionInstalled', 'version']),
    ...mapGetters('common', ['needToUpgrade', 'isAllowedIncognitoAccess', 'baseUrl']),
    addDisabled() {
      return !this.extensionInstalled || this.skipAccount || this.accountList.some((item) => !item.sessionInfo) || this.needToUpgrade;
    },
    verifyDisabled() {
      return !this.extensionInstalled || this.needToUpgrade || this.skipAccount || this.taskData.sandboxTargetServiceUrl === '' || !this.isAllowedIncognitoAccess;
    },
  },
  watch: {
    accountList: {
      handler(val) {
        const validAccounts = val.filter((item) => item.role && item.sessionInfo);
        const hasAdmin = validAccounts.some((item) => item.role === 'admin');

        if (hasAdmin || validAccounts.length >= 1) {
          this.$emit('next-disabled', false);
          this.showSkipAccount = false;
        } else if (!this.skipAccount) {
          this.$emit('next-disabled', true);
        }
      },
      deep: true,
      immediate: true,
    },
    skipAccount(val) {
      if (val) {
        this.$emit('next-disabled', false);
        this.accountList = [
          {
            role: '',
            id: crypto.randomUUID(),
            sessionInfo: null,
          },
        ];
      }
    },
  },
  created() {
    eventBus.$on('session', (data) => {
      const { id, sessionInfo, homePageUrl } = data;

      if (homePageUrl) {
        this.homePageUrl = homePageUrl;
      }

      this.accountList = this.accountList.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            sessionInfo,
          };
        }

        return item;
      });
    });
  },
  mounted() {
    if (!this.isAllowedIncognitoAccess) {
      this.countdown();
    }
  },
  methods: {
    handleAddAccount() {
      this.accountList.push({
        role: '',
        id: crypto.randomUUID(),
        sessionInfo: null,
      });
    },
    handleVerify(data, index) {
      const { role, id } = data;

      if (role === '') {
        this.$refs[`roleSelect-${index}`][0].handleFocus();
        return;
      }

      const roleLabel = this.options.find((item) => item.value === role)?.label ?? role;

      if (this.loginUrl) {
        eventBus.$emit(EventType.Extension, {
          action: 'create',
          payload: {
            baseUrl: this.baseUrl,
            targetServiceUrl: this.taskData.sandboxTargetServiceUrl,
            appTitle: process.env.VUE_APP_TITLE,
            accountInfo: {
              account: roleLabel,
              uuid: id,
            },
            url: this.loginUrl,
          },
        });
      }
    },
    handleDeleteAccount(index, id) {
      const defaultAccount = {
        role: '',
        id: crypto.randomUUID(),
        sessionInfo: null,
      };

      this.accountList.length === 1 ? this.accountList.splice(index, 1, defaultAccount) : this.accountList.splice(index, 1);

      // if (account?.[0].sessionInfo) {
      eventBus.$emit(EventType.Extension, {
        action: 'delete',
        payload: {
          uuid: id,
        },
      });
      // }
    },
    next() {
      const validAccounts = this.accountList.filter((item) => item.role && item.sessionInfo);
      const hasAdmin = validAccounts.some((item) => item.role === 'admin');

      if (this.skipAccount) {
        return {
          stage: 'TestAccount',
          data: {
            accountList: [],
            homePageUrl: this.homePageUrl,
          },
        };
      }

      if (validAccounts.length < 1 && !hasAdmin) {
        this.$message.error(this.$t('testAccount.errorMessage'));
        return;
      }

      return {
        stage: 'TestAccount',
        data: {
          accountList: validAccounts.map((item) => ({
            role: item.role,
            id: item.id,
            roleLabel: this.options.find((option) => option.value === item.role)?.label || item.role,
            sessionInfo: item.sessionInfo,
          })),
          homePageUrl: this.homePageUrl,
        },
      };
    },
    handleCheckAgain() {
      eventBus.$emit(EventType.Extension, { action: 'checkIncognito' });
      this.count = 10;
    },
    countdown() {
      if (this.isAllowedIncognitoAccess) {
        return;
      }

      if (this.count === 0) {
        this.handleCheckAgain();
        this.countdown();
        return;
      }

      setTimeout(() => {
        this.count -= 1;
        this.countdown();
      }, 1000);
    },
  },
};
</script>

<style lang="scss" scoped>
.testAccount {
  .accountIndex {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1.25rem;
    line-height: 1;
    border-radius: 50%;
    overflow: hidden;
    user-select: none;
    color: rgb(255, 255, 255);
    background-color: rgb(189, 189, 189);
    width: 36px;
    height: 36px;
  }
  .label {
    font-weight: bold;
    line-height: 40px;
    white-space: nowrap;
  }

  .incognito {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
  }

  .mr {
    display: inline-block;
    font-size: 20px;
    margin-left: 10px;
  }

  .lineHeight-40 {
    line-height: 40px;
  }

  .accountIcon {
    font-size: 20px;
    color: #909399;

    &.active {
      color: #67c23a;
    }
  }

  .accountList {
    margin-top: 20px;
    .accountItem {
      margin-top: 20px;
    }
  }

  .addAccount {
    margin-top: 20px;

    .el-icon-circle-plus {
      font-size: 20px;
      margin-right: 5px;
    }
  }

  .warningIcon {
    margin-right: 10px;
    color: black;
    font-size: 20px;
  }

  .uninstall {
    width: 80%;
    margin-top: 10px;
  }

  .uninstallText {
    font-size: 18px;
    font-weight: 500;
    color: #f56c6c;
  }

  .delIcon {
    margin-left: 10px;
    font-size: 20px;
    color: black;
  }

  .uninstallTag {
    width: 600px;
    padding: 20px;
    color: black;
    font-size: 16px;
    white-space: pre-wrap;
    margin-top: 20px;
    height: fit-content;
  }

  .link {
    display: inline-block;
    background: #000;
    color: #fff;
    text-decoration: none;
    padding: 0 8px;
    border-radius: 4px;
  }

  .skipAccount {
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 40px;
    left: 28%;

    .tipText {
      width: 400px;
      white-space: normal;
      margin-left: 20px;
    }
  }
}
</style>
<style lang="scss">
.incognitoTipText {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.urlTip {
  min-width: 300px !important;
  word-break: break-word !important;
}

.verifyTip.el-tooltip__popper {
  width: auto !important;
}
</style>